import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { cn } from '@/lib/utils'
import { SpinnerIosRegular } from '@fluentui/react-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { IconUnpinOutline } from '@icons/IconUnpinOutline'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { DefaultValues, useForm, useWatch } from 'react-hook-form'
import { FieldRenderer } from '../../component-library/FormComponent/FieldRenderer'
import {
  FormValues,
  InferFormValues,
  JsonFormPropertyV2,
  JsonFormSchemaV2,
} from '../../component-library/FormComponent/types'
import { cleanupFormData, createDefaultValues, generateZodSchema } from '../../component-library/FormComponent/utils'
import { isFieldVisible } from '../../component-library/FormComponent/fieldVisibility'
import { useConditionalRuleFields } from '../../component-library/FormComponent/useConditionalRuleFields'
import { TPageFilter } from '../types'
import { useAppPageState } from '../useAppPageState'
import clsx from 'clsx'
import { createPortal } from 'react-dom'
import { IconV2FilterOutline } from '@icons/IconV2FilterOutline'

// Define a type for the imperative handle
export type FormContentHandle = {
  reset: () => void
}

const AUTO_SUBMIT_DEBOUNCE_MS = 200

function isAutoSubmitEligibleField(
  property: JsonFormPropertyV2,
  data: Record<string, unknown>,
  visibleWhenMatchedIds: Set<string>
) {
  if (!isFieldVisible(property, data, visibleWhenMatchedIds) || property.readOnly) {
    return false
  }

  if (property.type === 'boolean') {
    return true
  }

  if (property.type === 'cascading') {
    return true
  }

  if ((property.type === 'string' || property.type === 'integer' || property.type === 'number') && property.choices) {
    return true
  }

  return false
}

function hasValueChanged(currentValue: unknown, initialValue: unknown) {
  return JSON.stringify(currentValue ?? null) !== JSON.stringify(initialValue ?? null)
}

export const PinnedFilterContent = forwardRef<
  FormContentHandle,
  {
    schema: JsonFormSchemaV2
    submitHandler: (data: InferFormValues<JsonFormSchemaV2>) => void
    resetDefaults?: InferFormValues<JsonFormSchemaV2>
    className?: string
    unPinFilter: (selectedF: TPageFilter) => void
    pinnedFilters: TPageFilter[]
    isSubmitting?: boolean
    actionsRef: React.RefObject<HTMLDivElement>
    autoSubmitPinnedFilters?: boolean
  }
>(function FilterContent(
  {
    schema,
    submitHandler,
    resetDefaults,
    className,
    unPinFilter,
    pinnedFilters,
    isSubmitting,
    actionsRef,
    autoSubmitPinnedFilters = false,
  },
  ref
) {
  const { isConfiguringLayout } = useAppPageState()
  const { properties, required } = schema
  const zodSchema = generateZodSchema(properties, required || [])

  // Compute schema defaults
  const schemaDefaults = useMemo(() => createDefaultValues(properties), [properties])

  // Merge persisted values with schema defaults - persisted values take precedence
  // This ensures the form initializes with the correct values from session storage
  const initialValues = useMemo(() => {
    if (resetDefaults && Object.keys(resetDefaults).length > 0) {
      return { ...schemaDefaults, ...resetDefaults }
    }
    return schemaDefaults
  }, [schemaDefaults, resetDefaults])

  const form = useForm<FormValues>({
    resolver: zodResolver(zodSchema),
    defaultValues: initialValues,
  })
  const watchedValues = useWatch({ control: form.control })
  const visibleWhenMatchedIds = useConditionalRuleFields(properties, 'visibleWhen', form.control)

  const { reset } = form
  const isResettingRef = useRef(false)
  const autoSubmitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const resetWithoutAutoSubmit = useCallback(
    (values: DefaultValues<FormValues>) => {
      isResettingRef.current = true
      reset(values)
      requestAnimationFrame(() => {
        isResettingRef.current = false
      })
    },
    [reset]
  )

  // Expose the reset method via useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      reset: () => resetWithoutAutoSubmit(schemaDefaults),
    }),
    [resetWithoutAutoSubmit, schemaDefaults]
  )

  // Track the previous resetDefaults to detect changes after initial mount
  const prevResetDefaultsRef = useRef(resetDefaults)

  useEffect(() => {
    // Only reset if resetDefaults has changed after the initial mount
    // This handles cases where persisted values update asynchronously (e.g., from storage events)
    if (resetDefaults && prevResetDefaultsRef.current !== resetDefaults) {
      const mergedValues = { ...schemaDefaults, ...resetDefaults }
      resetWithoutAutoSubmit(mergedValues)
    }
    prevResetDefaultsRef.current = resetDefaults
  }, [resetDefaults, resetWithoutAutoSubmit, schemaDefaults])

  const onSubmit = useCallback(
    (data: FormValues) => {
      const cleanedData = cleanupFormData(data)
      submitHandler(cleanedData as FormValues)
    },
    [submitHandler]
  )

  const { isDirty } = form.formState

  const leftFieldsToRender = useMemo(
    () =>
      properties.filter((property) =>
        pinnedFilters
          .filter((f) => f.pinned === 'left')
          .map((f) => f.id)
          .includes(property.id)
      ),
    [pinnedFilters, properties]
  )

  const visibleLeftFieldsToRender = useMemo(
    () =>
      leftFieldsToRender.filter((property) =>
        isFieldVisible(property, (watchedValues ?? {}) as Record<string, unknown>, visibleWhenMatchedIds)
      ),
    [leftFieldsToRender, watchedValues, visibleWhenMatchedIds]
  )

  const rightFieldsToRender = useMemo(
    () =>
      properties.filter((property) =>
        pinnedFilters
          .filter((f) => f.pinned === 'right')
          .map((f) => f.id)
          .includes(property.id)
      ),
    [pinnedFilters, properties]
  )

  const visibleRightFieldsToRender = useMemo(
    () =>
      rightFieldsToRender.filter((property) =>
        isFieldVisible(property, (watchedValues ?? {}) as Record<string, unknown>, visibleWhenMatchedIds)
      ),
    [rightFieldsToRender, watchedValues, visibleWhenMatchedIds]
  )

  const visiblePinnedFields = useMemo(
    () => [...visibleLeftFieldsToRender, ...visibleRightFieldsToRender],
    [visibleLeftFieldsToRender, visibleRightFieldsToRender]
  )

  const pinnedFieldIds = useMemo(() => visiblePinnedFields.map((property) => property.id), [visiblePinnedFields])

  const autoSubmitFieldIds = useMemo(
    () =>
      new Set(
        visiblePinnedFields
          .filter((property) =>
            isAutoSubmitEligibleField(
              property,
              (watchedValues ?? {}) as Record<string, unknown>,
              visibleWhenMatchedIds
            )
          )
          .map((property) => property.id)
      ),
    [visiblePinnedFields, watchedValues, visibleWhenMatchedIds]
  )

  const changedPinnedFieldIds = useMemo(
    () =>
      pinnedFieldIds.filter((fieldId) =>
        hasValueChanged(
          (watchedValues as Record<string, unknown> | undefined)?.[fieldId],
          (initialValues as Record<string, unknown>)[fieldId]
        )
      ),
    [initialValues, pinnedFieldIds, watchedValues]
  )

  const hasDirtyManualSubmitField = useMemo(
    () => changedPinnedFieldIds.some((fieldId) => !autoSubmitFieldIds.has(fieldId)),
    [autoSubmitFieldIds, changedPinnedFieldIds]
  )

  const shouldShowApplyButton = autoSubmitPinnedFilters ? hasDirtyManualSubmitField : isDirty

  // Keep latest eligibility in refs so the watch subscription is not torn down (and its
  // debounced submit cleared) on every value change — autoSubmitFieldIds updates when
  // watchedValues changes after PR 13215 conditional visibility support.
  const autoSubmitFieldIdsRef = useRef(autoSubmitFieldIds)
  autoSubmitFieldIdsRef.current = autoSubmitFieldIds
  const hasDirtyManualSubmitFieldRef = useRef(hasDirtyManualSubmitField)
  hasDirtyManualSubmitFieldRef.current = hasDirtyManualSubmitField
  const isConfiguringLayoutRef = useRef(isConfiguringLayout)
  isConfiguringLayoutRef.current = isConfiguringLayout

  useEffect(() => {
    if (!autoSubmitPinnedFilters) {
      return
    }

    const subscription = form.watch((_value, info) => {
      const changedFieldName = info.name

      if (
        !changedFieldName ||
        isConfiguringLayoutRef.current ||
        isResettingRef.current ||
        !autoSubmitFieldIdsRef.current.has(changedFieldName)
      ) {
        return
      }

      if (hasDirtyManualSubmitFieldRef.current) {
        return
      }

      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current)
      }

      autoSubmitTimeoutRef.current = setTimeout(() => {
        void form.handleSubmit(onSubmit)()
      }, AUTO_SUBMIT_DEBOUNCE_MS)
    })

    return () => {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current)
      }
      subscription.unsubscribe()
    }
  }, [autoSubmitPinnedFilters, form, onSubmit])

  const submitButton = isSubmitting ? (
    <LoadingButton />
  ) : (
    <div className="relative flex items-center justify-center p-1">
      <Button
        variant={shouldShowApplyButton ? 'default' : 'ghost'}
        disabled={!shouldShowApplyButton}
        className={clsx('h-6 text-xs', { 'sr-only': !shouldShowApplyButton })}
        onClick={form.handleSubmit(onSubmit)}
      >
        <IconV2FilterOutline />
        Apply ?
      </Button>
      {shouldShowApplyButton && (
        <div className="pointer-events-none absolute inset-[22%] z-50 size-[56%] animate-ping rounded border border-primary"></div>
      )}
    </div>
  )

  return (
    <div className="flex h-full flex-1">
      <Form {...form}>
        <form className={cn('flex flex-1 flex-row items-center gap-x-2', className)}>
          <div className="flex flex-1 items-center justify-between gap-x-2">
            <div className="flex items-center gap-2">
              {visibleLeftFieldsToRender.map((property) => {
                const filter = pinnedFilters.find((f) => f.id === property.id)
                if (!filter) {
                  return null
                }
                return (
                  <div className="flex items-center" key={filter.id}>
                    {isConfiguringLayout && (
                      <Button size="icon" className="size-5 rounded-full" onClick={() => unPinFilter(filter)}>
                        <IconUnpinOutline className="size-4" />
                      </Button>
                    )}
                    <FieldRenderer field={property} control={form.control} variant="row" />
                  </div>
                )
              })}
              {/* {submitButton} */}
            </div>
            <div className="flex items-center gap-2">
              {visibleRightFieldsToRender.map((property) => {
                const filter = pinnedFilters.find((f) => f.id === property.id)
                if (!filter) {
                  return null
                }
                return (
                  <div className="flex items-center" key={filter.id}>
                    {isConfiguringLayout && (
                      <Button size="icon" className="size-5 rounded-full" onClick={() => unPinFilter(filter)}>
                        <IconUnpinOutline className="size-4" />
                      </Button>
                    )}
                    <FieldRenderer field={property} control={form.control} variant="row" />
                  </div>
                )
              })}
              {/* {submitButton} */}
            </div>
          </div>
          {actionsRef.current && createPortal(submitButton, actionsRef.current)}
        </form>
      </Form>
    </div>
  )
})

export function LoadingButton() {
  return (
    <div className="flex w-full justify-center">
      <SpinnerIosRegular fontSize={20} className="animate-spin" />
    </div>
  )
}
